import type { AgendaItem, AgendaItemStatusUpdate } from "./types";

/** Merge live per-item status from a /status poll into the existing agenda
 *  items. Patches only statusId + acceptingSpeakers; leaves other fields
 *  intact. Returns the same `items` reference on a no-op so React skips a
 *  re-render, plus the ids that flipped accepting → not-accepting this poll
 *  (used to drop now-closed selections and surface the banner). AB#8889 */
export function reconcileAgendaItemStatuses(
  items: AgendaItem[],
  updates: AgendaItemStatusUpdate[]
): { items: AgendaItem[]; changed: boolean; newlyClosedIds: number[] } {
  if (!updates || updates.length === 0) {
    return { items, changed: false, newlyClosedIds: [] };
  }
  const byId = new Map<number, AgendaItemStatusUpdate>();
  updates.forEach((u) => byId.set(u.id, u));
  let changed = false;
  const newlyClosedIds: number[] = [];
  const next = items.map((it) => {
    const u = byId.get(it.id);
    if (!u) return it;
    const nextStatusId = u.statusId ?? 0; // statusId is 0-based on AgendaItem
    const nextAccepting = u.acceptingSpeakers;
    const nextFull = u.full === true;
    const nextClosedReason = u.closedReason ?? it.closedReason;
    // Only a true→false transition is "newly closed" — a staying-closed item
    // must not re-fire the banner. Covers an item filling up (accepting→full),
    // since full folds into acceptingSpeakers=false server-side.
    if (it.acceptingSpeakers !== false && nextAccepting === false) {
      newlyClosedIds.push(it.id);
    }
    if (
      it.statusId === nextStatusId &&
      it.acceptingSpeakers === nextAccepting &&
      (it.full ?? false) === nextFull &&
      it.closedReason === nextClosedReason
    ) {
      return it;
    }
    changed = true;
    return {
      ...it,
      statusId: nextStatusId,
      acceptingSpeakers: nextAccepting,
      full: nextFull,
      closedReason: nextClosedReason
    };
  });
  return { items: changed ? next : items, changed, newlyClosedIds };
}
